if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Desktop thumbnail gallery: quietly load every slide image once the
        // page has finished loading, so switching is instant most of the time.
        if (this.isDesktopSingle()) {
          const preloadAll = () => {
            const run = () => this.elements.viewer.querySelectorAll('li.product__media-item img').forEach((img) => this.warmImage(img));
            if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2000 });
            else setTimeout(run, 300);
          };
          if (document.readyState === 'complete') preloadAll();
          else window.addEventListener('load', preloadAll, { once: true });
        }
      }

      // One image at a time on desktop (thumbnail layouts), with a fade
      isDesktopSingle() {
        return this.mql.matches && (this.dataset.desktopLayout || '').includes('thumbnail');
      }

      // Start loading an image now (it may be lazy and hidden) and resolve
      // once it is fully decoded and ready to paint
      warmImage(img) {
        if (!img) return Promise.resolve();
        if (img.loading === 'lazy') img.loading = 'eager';
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        if (img.decode) return img.decode().catch(() => {});
        return new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        // Desktop: don't switch to an image that hasn't loaded yet. Wait for
        // it (up to 2.5s), so the fade always lands on a finished image
        // instead of a blank slide that pops in later. Only the latest
        // request wins if the arrows are clicked again while waiting.
        this.hlToken = (this.hlToken || 0) + 1;
        const token = this.hlToken;
        const target =
          this.elements.viewer.querySelector(`li[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        const img = target && !target.classList.contains('is-active') ? target.querySelector('img') : null;

        if (this.isDesktopSingle() && img && !(img.complete && img.naturalWidth > 0)) {
          this.hlPendingMediaId = mediaId;
          // Thumbnail highlight moves right away so the click feels answered
          if (this.elements.thumbnails) {
            this.setActiveThumbnail(this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`));
          }
          Promise.race([this.warmImage(img), new Promise((r) => setTimeout(r, 2500))]).then(() => {
            if (token !== this.hlToken) return;
            this.hlPendingMediaId = null;
            this.applyActiveMedia(mediaId, prepend);
          });
          return;
        }

        this.hlPendingMediaId = null;
        this.applyActiveMedia(mediaId, prepend);
      }

      applyActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        const previousMedia = this.elements.viewer.querySelector('li.product__media-item.is-active');
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.animateSwitch(previousMedia, activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        // Get the slides either side ready for the next click
        if (this.isDesktopSingle()) {
          const items = Array.from(this.elements.viewer.querySelectorAll('li.product__media-item'));
          const i = items.indexOf(activeMedia.closest('li.product__media-item') || activeMedia);
          [i - 1, i + 1].forEach((n) => {
            const li = items[(n + items.length) % items.length];
            if (li) this.warmImage(li.querySelector('img'));
          });
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      // Theme editor > Product information > Gallery animation.
      // The old image stays on screen underneath (.hl-leaving) while the new
      // one animates in on top (.hl-entering), so there's never a blank frame.
      animateSwitch(from, to) {
        const mode = this.dataset.anim || 'fade';
        if (!this.isDesktopSingle() || mode === 'fade' || !from || !to || from === to) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const items = Array.from(this.elements.viewer.querySelectorAll('li.product__media-item'));
        const a = items.indexOf(from);
        const b = items.indexOf(to);
        let forward = b > a;
        if (a === items.length - 1 && b === 0) forward = true;
        if (a === 0 && b === items.length - 1) forward = false;
        // RTL: "next" comes in from the left
        this.style.setProperty('--hl-gal-dx', forward ? '-28px' : '28px');

        clearTimeout(this.hlAnimTimer);
        this.elements.viewer.querySelectorAll('.hl-leaving, .hl-entering').forEach((el) => {
          el.classList.remove('hl-leaving', 'hl-entering');
        });
        void to.offsetWidth; // restart the animation
        from.classList.add('hl-leaving');
        to.classList.add('hl-entering');

        let dur = parseFloat(getComputedStyle(this).getPropertyValue('--hl-gal-dur')) || 450;
        if (mode === 'zoom') dur *= 1.6; // the zoom inside the frame runs a little longer
        this.hlAnimTimer = setTimeout(() => {
          from.classList.remove('hl-leaving');
          to.classList.remove('hl-entering');
        }, dur + 50);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}